import * as React from 'react';
import { Alert } from 'rcd-cpp-ui/ui/alert/Alert';
import { useSelector } from '../../../utils/useSelector';
import { excludeEmptyFilters } from '../../../utils';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Cancel, Primary } from 'rcd-cpp-ui/ui/button';
import { ModalGroup } from '../../ModalGroup/modalGroup';
import { Loader } from '../../Loader/loader';
import TooltipPopover from '../../Popover/popover';
import { useActions as useViewActions } from '../../View/viewActions';
import { Filters } from '../../Filters/filters';
import './requiredFiltersError.scss';
import { View } from '../../../types/View';
import { SchemaField } from '../../../types/Metadata';
import { findOne } from 'rcd-cpp-workspace/utils/array';

type Props = { fields: SchemaField[] };

export const RequiredFiltersError = ({ fields }: Props) => {
  const [showRequiredFiltersModal, setShowRequiredFiltersModal] =
    React.useState(fields?.length > 0);
  if (!fields?.length) return null;

  return (
    <div>
      <Alert type="error">
        <div>
          The below filters are required in order to leverage this view.&nbsp;
          <u
            onClick={() => setShowRequiredFiltersModal(true)}
            style={{ cursor: 'pointer' }}
          >
            View required filters
          </u>
        </div>
        {fields.map(f => {
          return <div key={f.key}> - {f.displayName}</div>;
        })}
      </Alert>
      {showRequiredFiltersModal && (
        <RequiredFiltersModal
          onClose={() => setShowRequiredFiltersModal(false)}
          fields={fields}
        />
      )}
    </div>
  );
};

type ModalProps = { onClose: () => void } & Props;

export const RequiredFiltersModal = ({ onClose, fields }: ModalProps) => {
  const [stateFilters, setStateFilters] = React.useState<View.FiltersField[]>(
    fields.map(f => ({
      field: f.key,
      isRequired: true,
      uuid: Math.random().toString(10)
    }))
  );

  const viewRefId = useSelector(s => s.view?.selected?.props?.refId);
  const filters = useSelector(s => s.view?.filters);
  const getAPI = useSelector(s => s?.app?.elFn?.getAPI);
  const { setFilters } = useViewActions();
  const preappliedFilters = useSelector(
    s => s?.view?.selected?.props?.preApplyFilters
  );
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const defaultFilters = useSelector(
    s => s.planning?.selectedWorkspace?.setting?.props?.defaultFilters
  );

  const nonEmptyFilters = excludeEmptyFilters(stateFilters);
  const handleSetFilter = filter => {
    setStateFilters(prevFilters => {
      const newFilters = prevFilters.map(f =>
        f.field === filter.field ? filter : f
      );
      if (!newFilters.some(f => f.field === filter.field)) {
        newFilters.push(filter);
      }
      return newFilters;
    });
  };
  return (
    <div className="slider-activator">
      <Modal
        id="requiredFiltersModal"
        header="Required Filters"
        onClose={onClose}
        footer={
          <Buttons>
            <Cancel onClick={onClose} />
            <Primary
              label="Apply"
              onClick={() => {
                const newFilters = [...stateFilters];
                filters.forEach(f => {
                  const filter = findOne(stateFilters, { field: f.field });
                  if (!filter) newFilters.push(f);
                });
                setFilters(newFilters);
              }}
              disabled={stateFilters.length != nonEmptyFilters.length}
            />
          </Buttons>
        }
      >
        <div>
          Please make a selection from the filter(s) below in order to leverage
          this view.
        </div>
        <Loader id="requiredFiltersLoader" />
        <ModalGroup
          id="requiredFilterGroup"
          label={
            <>
              Select Required Filters{' '}
              <span>
                <TooltipPopover
                  pointerPosition="right"
                  content={
                    'Select which filter(s) users must leverage when using this view. Users will be prompted to make a selection from this filter(s) in order to use this view.'
                  }
                  type="blueinfo"
                />
              </span>
            </>
          }
        >
          <Filters
            getAPI={getAPI}
            setFilter={handleSetFilter}
            schemas={schemas}
            filters={stateFilters}
            defaultFilters={defaultFilters}
            preappliedFilters={preappliedFilters}
            viewRefId={viewRefId}
            seeAllFilters={true}
            location="view"
          />
        </ModalGroup>
      </Modal>
    </div>
  );
};
